import React, { useEffect, useMemo, useState } from 'react';
import { normalizeWordKey } from '@/services/oneWordRecordsService';
import { OneWordRace, RaceRange, getOneWordRaces } from '@/services/oneWordRacesService';
import OneWordLetterErrorChart from './OneWordLetterErrorChart';

interface Props {
  uid?: string;
  word: string;
}

type Scope = 'word' | 'all';
const RANGES: RaceRange[] = ['day', 'week', 'month', 'all'];

const fmtMs = (ms: number): string => {
  if (!ms || !isFinite(ms) || ms <= 0) return '—';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(2)} s`;
};

const mean = (nums: number[]): number => {
  const valid = nums.filter((n) => n > 0);
  return valid.length ? valid.reduce((a, b) => a + b, 0) / valid.length : 0;
};

/**
 * Training Statistics — averages/totals for this word's One Word training, with a
 * This word ⇄ All switch to see overall growth, plus the shared error-trend chart.
 */
const OneWordTrainingStats: React.FC<Props> = ({ uid, word }) => {
  const [scope, setScope] = useState<Scope>('word');
  const [range, setRange] = useState<RaceRange>('all');
  const [races, setRaces] = useState<OneWordRace[]>([]);
  const wordKey = useMemo(() => normalizeWordKey(word), [word]);

  useEffect(() => {
    if (!uid) return;
    let cancelled = false;
    getOneWordRaces(uid, { wordKey: scope === 'word' ? wordKey : undefined, range }).then((rows) => {
      if (!cancelled) setRaces(rows);
    });
    return () => { cancelled = true; };
  }, [uid, wordKey, scope, range]);

  const summary = useMemo(() => ({
    racesCount: races.length,
    avgBestLap: mean(races.map((r) => r.bestLapMs)),
    avgFlow: mean(races.map((r) => r.bestStreak)),
    avgLaps: mean(races.map((r) => r.lapsCompleted)),
    totalLaps: races.reduce((a, r) => a + (r.lapsCompleted || 0), 0),
    totalMistakes: races.reduce((a, r) => a + (r.totalMistakes || 0), 0),
  }), [races]);

  return (
    <div className="owts">
      <div className="owts-head">
        <div className="owts-heading">Training Statistics</div>
        <div className="owts-scope">
          <button className={`owts-scope-btn ${scope === 'word' ? 'active' : ''}`} onClick={() => setScope('word')}>
            This word
          </button>
          <button className={`owts-scope-btn ${scope === 'all' ? 'active' : ''}`} onClick={() => setScope('all')}>
            All
          </button>
        </div>
      </div>
      {scope === 'word' && <div className="owts-word">"{word}"</div>}

      <div className="owts-grid">
        <div className="owts-cell"><span className="owts-l">Races</span><span className="owts-v">{summary.racesCount}</span></div>
        <div className="owts-cell"><span className="owts-l">Avg Lap</span><span className="owts-v">{fmtMs(summary.avgBestLap)}</span></div>
        <div className="owts-cell"><span className="owts-l">Avg Flow</span><span className="owts-v">{summary.avgFlow.toFixed(1)}</span></div>
        <div className="owts-cell"><span className="owts-l">Avg Laps</span><span className="owts-v">{summary.avgLaps.toFixed(1)}</span></div>
        <div className="owts-cell"><span className="owts-l">Total Laps</span><span className="owts-v">{summary.totalLaps}</span></div>
        <div className="owts-cell"><span className="owts-l">Mistakes</span><span className="owts-v">{summary.totalMistakes}</span></div>
      </div>

      <div className="owts-chart-head">
        <span className="owts-chart-title">Error trend</span>
        <div className="owts-range">
          {RANGES.map((r) => (
            <button key={r} className={`owts-range-btn ${range === r ? 'active' : ''}`} onClick={() => setRange(r)}>
              {r === 'all' ? 'All' : r[0].toUpperCase() + r.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <OneWordLetterErrorChart races={races} topN={7} height={180} />

      <style>{`
        .owts {
          display: flex;
          flex-direction: column;
          gap: 8px;
          padding: 14px;
          background: rgba(17, 24, 39, 0.8);
          border-radius: 12px;
          border: 1px solid rgba(75, 85, 99, 0.3);
          backdrop-filter: blur(10px);
          width: 300px;
          box-sizing: border-box;
        }
        .owts-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
        }
        .owts-heading {
          font-size: 0.92rem;
          font-weight: 700;
          color: #ffffff;
        }
        .owts-scope, .owts-range { display: flex; gap: 2px; }
        .owts-scope-btn, .owts-range-btn {
          padding: 2px 6px;
          font-size: 0.6rem;
          color: #94a3b8;
          background: rgba(0, 0, 0, 0.25);
          border: 1px solid rgba(75, 85, 99, 0.3);
          border-radius: 4px;
          cursor: pointer;
        }
        .owts-scope-btn.active, .owts-range-btn.active {
          color: #ffffff;
          background: rgba(59, 130, 246, 0.3);
          border-color: rgba(59, 130, 246, 0.5);
        }
        .owts-word {
          font-size: 0.76rem;
          color: #cbd5e1;
          margin-top: -2px;
        }
        .owts-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 5px;
        }
        .owts-cell {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 5px 2px;
          background: rgba(0, 0, 0, 0.25);
          border-radius: 6px;
        }
        .owts-l {
          font-size: 0.55rem;
          color: #94a3b8;
          text-transform: uppercase;
          letter-spacing: 0.04em;
        }
        .owts-v {
          font-size: 0.82rem;
          font-weight: 700;
          color: #f8fafc;
          font-variant-numeric: tabular-nums;
        }
        .owts-chart-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
        }
        .owts-chart-title {
          font-size: 0.64rem;
          color: #94a3b8;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }
      `}</style>
    </div>
  );
};

export default OneWordTrainingStats;
